import { Reducer } from "redux";

import BimtrackHub from "../../models/api/bimtrack_hub";
import BimtrackProject from "../../models/api/bimtrack_project";
import PlannedBuildingElement from "../../models/domain/planned_building_element";
import { BIMTRACK_HUB_SELECTED, BimtrackHubSelectedEvent } from "../../events/viewer/bimtrack_hub_selected";
import { BIMTRACK_HUBS_LOADED, BimtrackHubsLoadedEvent } from "../../events/loaded/bimtrack_hubs_loaded";
import { BIMTRACK_ISSUE_CREATION_DIALOG_DISMISSED, BimtrackIssueCreationDialogDismissedEvent } from "../../events/viewer/bimtrack_issue_creation_dialog_dismissed";
import { BIMTRACK_ISSUE_CREATION_DIALOG_SHOWN, BimtrackIssueCreationDialogShownEvent } from "../../events/viewer/bimtrack_issue_creation_dialog_shown";
import { BIMTRACK_PROJECT_LOADED, BimtrackProjectLoadedEvent } from "../../events/loaded/bimtrack_project_loaded";
import { BIMTRACK_PROJECTS_LOADED, BimtrackProjectsLoadedEvent } from "../../events/loaded/bimtrack_projects_loaded";
import { BIMTRACK_PROJECT_SELECTED, BimtrackProjectSelectedEvent } from "../../events/viewer/bimtrack_project_selected";
import { isRoutingEvent } from "../routing_events";

import type { Dictionary } from "underscore";

export type BimtrackIssueCreationDialogStore = {
  open: boolean
  project?: BimtrackProject
  selectedElement?: PlannedBuildingElement
  globalId?: string
  hubs?: BimtrackHub[]
  projects?: Dictionary<BimtrackProject[]>
  selectedHubId?: string
  selectedProjectId?: number
}

type Events =
    | BimtrackIssueCreationDialogShownEvent
    | BimtrackIssueCreationDialogDismissedEvent
    | BimtrackProjectLoadedEvent
    | BimtrackHubsLoadedEvent
    | BimtrackProjectsLoadedEvent
    | BimtrackHubSelectedEvent
    | BimtrackProjectSelectedEvent

const withoutLoadedProject = (bimtrackIssueCreationDialog: BimtrackIssueCreationDialogStore): BimtrackIssueCreationDialogStore => {
  // eslint-disable-next-line no-undefined
  return { ...bimtrackIssueCreationDialog, project: undefined, selectedProjectId: undefined };
};

const reduceBimtrackIssueCreationDialog: Reducer<BimtrackIssueCreationDialogStore, Events> = (bimtrackIssueCreationDialog = { open: false }, event) => {
  if (isRoutingEvent(event)) {
    return { open: false };
  }
  switch (event.type) {
    case BIMTRACK_ISSUE_CREATION_DIALOG_SHOWN: {
      return {
        ...withoutLoadedProject(bimtrackIssueCreationDialog),
        open: true,
        globalId: event.payload.globalId,
        selectedElement: event.payload.selectedElement
      };
    }
    case BIMTRACK_ISSUE_CREATION_DIALOG_DISMISSED: {
      return {
        ...bimtrackIssueCreationDialog,
        open: false,
      };
    }
    case BIMTRACK_PROJECT_LOADED: {
      // Ignore a late response for a project that is no longer selected.
      if (event.payload?.Id !== bimtrackIssueCreationDialog.selectedProjectId) {
        return bimtrackIssueCreationDialog;
      }
      return {
        ...bimtrackIssueCreationDialog,
        project: event.payload
      };
    }
    case BIMTRACK_HUBS_LOADED: {
      return {
        ...bimtrackIssueCreationDialog,
        hubs: event.payload
      };
    }
    case BIMTRACK_PROJECTS_LOADED: {
      return {
        ...bimtrackIssueCreationDialog,
        projects: {
          ...bimtrackIssueCreationDialog.projects,
          [event.payload.hubId]: event.payload.projects
        }
      };
    }
    case BIMTRACK_HUB_SELECTED: {
      return {
        ...withoutLoadedProject(bimtrackIssueCreationDialog),
        selectedHubId: event.payload
      };
    }
    case BIMTRACK_PROJECT_SELECTED: {
      return {
        ...withoutLoadedProject(bimtrackIssueCreationDialog),
        selectedProjectId: event.payload
      };
    }
    default: {
      return bimtrackIssueCreationDialog;
    }
  }
};

export default reduceBimtrackIssueCreationDialog;
